'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Star, Calendar, ArrowRight, Heart, ChevronRight, Shield } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface Hotel {
  id: string;
  name: string;
  location: string;
  city: string;
  stars: number;
  rating: number;
  reviews: number;
  price_per_night: number;
  image_url: string;
  amenities: string[];
  is_featured: boolean;
  is_verified: boolean;
  room_types: string[];
  description: string;
}

const HOTELS: Hotel[] = [
{ id: '1', name: 'Serena Hotel Nairobi', location: 'Processional Way, Nairobi', city: 'Nairobi', stars: 5, rating: 4.8, reviews: 1240, price_per_night: 18500, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_197cc4286-1772473839089.png", amenities: ['WiFi', 'Pool', 'Gym', 'Restaurant', 'Parking'], is_featured: true, is_verified: true, room_types: ['Standard', 'Deluxe', 'Suite'], description: 'Iconic 5-star hotel in the heart of Nairobi with world-class amenities.' },
{ id: '2', name: 'Sarova Whitesands', location: 'Bamburi Beach, Mombasa', city: 'Mombasa', stars: 4, rating: 4.6, reviews: 890, price_per_night: 12000, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1673a68bc-1772401813015.png", amenities: ['WiFi', 'Beach', 'Pool', 'Restaurant', 'Spa'], is_featured: true, is_verified: true, room_types: ['Standard', 'Ocean View', 'Suite'], description: 'Beachfront resort with stunning Indian Ocean views and private beach access.' },
{ id: '3', name: 'Boma Inn Nairobi', location: 'Milimani Road, Nairobi', city: 'Nairobi', stars: 3, rating: 4.2, reviews: 456, price_per_night: 6500, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1275d15af-1769471673579.png", amenities: ['WiFi', 'Restaurant', 'Parking', 'Conference'], is_featured: false, is_verified: true, room_types: ['Standard', 'Executive'], description: 'Comfortable business hotel with excellent conference facilities.' },
{ id: '4', name: 'Kisumu Hotel', location: 'Oginga Odinga Street, Kisumu', city: 'Kisumu', stars: 3, rating: 4.0, reviews: 234, price_per_night: 4500, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13bbf16b6-1778876335168.png", amenities: ['WiFi', 'Restaurant', 'Parking'], is_featured: false, is_verified: true, room_types: ['Standard', 'Deluxe'], description: 'Lakeside hotel with beautiful views of Lake Victoria.' },
{ id: '5', name: 'Fairmont The Norfolk', location: 'Harry Thuku Road, Nairobi', city: 'Nairobi', stars: 5, rating: 4.9, reviews: 2100, price_per_night: 25000, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15ab35170-1784530799179.png", amenities: ['WiFi', 'Pool', 'Gym', 'Spa', 'Restaurant', 'Bar'], is_featured: true, is_verified: true, room_types: ['Classic', 'Deluxe', 'Suite', 'Presidential'], description: 'Historic luxury hotel, a Nairobi landmark since 1904.' },
{ id: '6', name: 'Nakuru Milimani Hotel', location: 'Milimani, Nakuru', city: 'Nakuru', stars: 3, rating: 3.9, reviews: 178, price_per_night: 3800, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17f6529be-1784530799170.png", amenities: ['WiFi', 'Restaurant', 'Parking', 'Garden'], is_featured: false, is_verified: false, room_types: ['Standard', 'Family'], description: 'Peaceful hotel near Lake Nakuru National Park.' }];


const CITIES = ['All Cities', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret'];
const STAR_FILTERS = ['Any Stars', '5 Stars', '4 Stars', '3 Stars'];
const AMENITY_FILTERS = ['WiFi', 'Pool', 'Gym', 'Spa', 'Beach', 'Parking'];

export default function HotelsLandingPage() {
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('All Cities');
  const [stars, setStars] = useState('Any Stars');
  const [selectedAmenities, setSelectedAmenities] = useState<Set<string>>(new Set());
  const [maxPrice, setMaxPrice] = useState('');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');

  const toggleAmenity = useCallback((a: string) => {
    setSelectedAmenities((prev) => {const n = new Set(prev);n.has(a) ? n.delete(a) : n.add(a);return n;});
  }, []);

  const filtered = HOTELS.filter((h) => {
    const matchSearch = !search || h.name.toLowerCase().includes(search.toLowerCase()) || h.location.toLowerCase().includes(search.toLowerCase());
    const matchCity = city === 'All Cities' || h.city === city;
    const matchStars = stars === 'Any Stars' || h.stars === Number(stars[0]);
    const matchAmenities = selectedAmenities.size === 0 || [...selectedAmenities].every((a) => h.amenities.includes(a));
    const matchPrice = !maxPrice || h.price_per_night <= Number(maxPrice);
    return matchSearch && matchCity && matchStars && matchAmenities && matchPrice;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0e00 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #FF6D00 0%, transparent 50%), radial-gradient(circle at 70% 30%, #FFB300 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
              style={{ background: 'rgba(255,109,0,0.15)', color: '#FF6D00', border: '1px solid rgba(255,109,0,0.3)' }}>
                <Star size={12} fill="#FF6D00" /> {890 .toLocaleString()} Hotels Across Kenya
              </div>
              <Link href="/create-listing?type=hotel" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#FF6D00', color: '#fff' }}>
                + List Hotel
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Book Your <span style={{ color: '#FF6D00' }}>Perfect Stay</span>
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              From budget guesthouses to 5-star resorts. Best rates guaranteed.
            </p>
            {/* Search + dates */}
            <div className="flex flex-col gap-2 max-w-lg mx-auto">
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Hotel name or location…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full pl-9 pr-3 py-3 rounded-2xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: checkIn ? '#fff' : 'rgba(255,255,255,0.4)', colorScheme: 'dark' }} />
                </div>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full pl-9 pr-3 py-3 rounded-2xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: checkOut ? '#fff' : 'rgba(255,255,255,0.4)', colorScheme: 'dark' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City filter */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CITIES.map((c) =>
            <button key={c} onClick={() => setCity(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: city === c ? 'rgba(255,109,0,0.15)' : 'rgba(255,255,255,0.04)', color: city === c ? '#FF6D00' : 'rgba(255,255,255,0.55)', border: `1px solid ${city === c ? 'rgba(255,109,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {STAR_FILTERS.slice(1).map((s) =>
            <button key={s} onClick={() => setStars(stars === s ? 'Any Stars' : s)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: stars === s ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: stars === s ? '#FFB300' : 'rgba(255,255,255,0.55)', border: `1px solid ${stars === s ? 'rgba(255,179,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {s}
              </button>
            )}
          </div>
          {/* Amenity pills */}
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pt-2">
            {AMENITY_FILTERS.map((a) =>
            <button key={a} onClick={() => toggleAmenity(a)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: selectedAmenities.has(a) ? 'rgba(255,109,0,0.15)' : 'rgba(255,255,255,0.04)', color: selectedAmenities.has(a) ? '#FF6D00' : 'rgba(255,255,255,0.45)', border: `1px solid ${selectedAmenities.has(a) ? 'rgba(255,109,0,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {a}
              </button>
            )}
            <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max KES/night"
            className="w-28 px-3 py-1.5 rounded-xl text-xs outline-none flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FF6D00', fontWeight: 700 }}>{filtered.length}</span> hotels found
            </span>
            <Link href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#FF6D00', color: '#fff' }}>
              List Your Hotel <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-4">
            {filtered.map((hotel) =>
            <div key={hotel.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${hotel.is_featured ? 'rgba(255,109,0,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="flex flex-col sm:flex-row">
                  <div className="relative sm:w-48 h-44 sm:h-auto flex-shrink-0">
                    <img src={hotel.image_url} alt={`${hotel.name} exterior`}
                  className="w-full h-full object-cover" loading="lazy" />
                    {hotel.is_featured &&
                  <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                  style={{ background: '#FF6D00', color: '#fff' }}>Featured</span>
                  }
                    <button onClick={() => toggleSave(hotel.id)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: savedIds.has(hotel.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                      <Heart size={12} fill={savedIds.has(hotel.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                    </button>
                  </div>
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-700 text-white">{hotel.name}</h3>
                          {hotel.is_verified && <Shield size={12} style={{ color: '#00C853' }} />}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{hotel.location}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-800" style={{ color: '#FF6D00' }}>KES {hotel.price_per_night.toLocaleString()}</p>
                        <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>per night</p>
                      </div>
                    </div>
                    {/* Stars + rating */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex gap-0.5">
                        {Array.from({ length: hotel.stars }).map((_, i) =>
                      <Star key={i} size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                      )}
                      </div>
                      <span className="text-xs font-700" style={{ color: '#FFB300' }}>{hotel.rating}</span>
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>({hotel.reviews} reviews)</span>
                    </div>
                    <p className="text-xs mt-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{hotel.description}</p>
                    {/* Amenities */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {hotel.amenities.slice(0, 4).map((a) =>
                    <span key={a} className="text-[10px] px-1.5 py-0.5 rounded-md"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>{a}</span>
                    )}
                    </div>
                    <Link href={`/listing/hotels/${hotel.id}`}
                  className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-xl text-xs font-700"
                  style={{ background: '#FF6D00', color: '#fff' }}>
                      View & Book <ChevronRight size={11} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(255,109,0,0.1), rgba(255,179,0,0.1))', border: '1px solid rgba(255,109,0,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {890 .toLocaleString()} Hotels</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to book, manage reservations, and access exclusive member rates.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#FF6D00', color: '#fff' }}>
              <Star size={16} /> Book a Hotel
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}